import { Observable, fromEvent, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, startWith } from 'rxjs/operators';
import { WOptionOrString } from './w-option';
import { WUniversalModule, document, isBrowser, window } from './w-universal';
import info_json from './tmp/info.json';
import { Injectable, SimpleChanges } from '@angular/core';

export const MutationObserverStub = { observe: () => { }, disconnect: () => { } };

export const ngstyle = 'color: white; background-color: #fd7e14; padding: 2px 4px; border-radius: 4px;';
export const ngErrorStyle = 'color: white; background-color: #fd2f14ff; padding: 2px 4px; border-radius: 4px;';
console.log('%cw-angular', ngstyle, `${info_json.version}, ${info_json.timestamp}`);

class Lib {
  // display:none if no facet provided or it's content is empty
  static displayFacet(el: HTMLElement): boolean {
    const facet = el.querySelector('f-facet') as HTMLElement;
    return facet && (facet.children.length > 0 || facet.textContent !== '');
  }
  static hideContent(el: HTMLElement, selector = '*', checkChildren = true): boolean {
    // hide if no matching elements (or all of them are empty)
    if (!el) { return true; }
    const facets = el.querySelectorAll(selector) as NodeListOf<Element>;
    return (
      !el.textContent && (facets.length === 0 ||
        (checkChildren && Array.from(facets).every(f => (f.children.length === 0 && f.textContent === ''))))
    );
  }
}
export enum CLF_2_MAT {
  // buttonType -> mat icon ligature
  edit = 'edit',
  delete = 'delete',
  copy = 'content_copy',
  up = 'arrow_upward',
  down = 'arrow_downward',
  addressbook = 'contacts',
  search = 'search',
  calendar = 'calendar_today',
  dropdown = 'expand_more',
  download = 'download'
}
export const MAT_ICON_NAMES = `add,
  arrow_back,
  arrow_downward,
  arrow_forward,
  arrow_upward,
  beenhere,
  bookmark,
  calendar_month,
  calendar_today,
  check_box,
  check_box_outline_blank,
  chevron_right,
  chevron_left,
  close,
  contact_mail,
  contacts,
  content_copy,
  delete,
  done,
  download,
  drag_indicator,
  edit,
  error,
  expand_more,
  expand_less,
  help_center, 
  info,
  keyboard_double_arrow_right,
  keyboard_double_arrow_left,
  keyboard_double_arrow_up,
  keyboard_double_arrow_down,
  language,
  list,
  lock,
  lock_open_right, 
  more_horiz,
  notifications,
  open_in_new,
  person,
  priority_high,
  radio_button_checked,
  radio_button_unchecked,
  refresh,
  search,
  shopping_basket,
  translate,
  star,
  unfold_more,
  visibility,
  visibility_off,
  warning`.split(',').map(n => n.trim());
export const MAT_ICONS = MAT_ICON_NAMES.reduce((m, i) => {
  m[i] = i;
  return m;
}, {} as Record<string, string>);
// valid CLF button types
export type ButtonType = keyof typeof CLF_2_MAT | 'text' | 'header' | 'tertiary' | 'tmview' | keyof typeof MAT_ICONS;

export const nodeSignature = (e: Node) => {
  switch (e.nodeType) {
    case Node.TEXT_NODE:
      return `[text]=${e.textContent}`;
    case Node.COMMENT_NODE:
      return `[comment]=${e.textContent}`;
    case Node.ELEMENT_NODE:
      const el = e as HTMLElement;
      return `${el.tagName} ${el.classList ? Array.from(el.classList) : ''}`;
    default: return `[${e.nodeType}]=${e.textContent}`;
  }
};
export const hideEmptyContentOf = Lib.hideContent;
export const observeOnMutation = (target: Element,
  config?: MutationObserverInit): Observable<MutationRecord[]> => {
  return new Observable((observer) => {
    const mutation = isBrowser ? new MutationObserver((mutations, instance) => {
      observer.next(mutations);
    }) : MutationObserverStub;
    mutation.observe(target, config);
    const unsubscribe = () => {
      mutation.disconnect();
    };
    return unsubscribe;
  });
};
export const doOnMutationType = (target: Element,
  config: any, types: MutationRecordType[]): Observable<MutationRecord[]> => {
  return observeOnMutation(target, config).pipe(
    filter((mm: MutationRecord[]) =>
      mm.some(m => !types || types.length === 0 || types.includes(m.type)))
  );
};
export const dispatchCustomEvent = (name: string, detail: any) => {
  // IE compatible
  let e: CustomEvent;
  if (typeof window.CustomEvent === 'function') {
    // regular browser
    e = new CustomEvent(name, {
      bubbles: true,
      detail
    });
  } else {
    // IE
    e = document.createEvent('CustomEvent');
    e.initCustomEvent(name, true, false, detail);
  }
  document.dispatchEvent(e);
};

export const arraysEqual = (a1: any[], a2?: any[]) => {
  if (!a2 || a2.length !== a1.length) {
    return false;
  } else {
    return a2.every(a => a1.includes(a)) && a1.every(a => a2.includes(a));
  }
};
export const isArrowKey = (event: KeyboardEvent) => event.key === 'ArrowUp' || event.key === 'ArrowDown';
export const isModifierKey = (event: KeyboardEvent) => event.altKey || event.ctrlKey
  || event.key === 'Alt' || event.key === 'Control' || event.key === 'Shift' || event.key === 'AltGraph'
  || event.key === 'Meta' || event.key === 'Meta';

export const copyDeep = <T>(o: T | T[], asType?: any): any => {
  if (Array.isArray(o)) {
    return o.map(v => copyDeep<T>(v));
  } else if (o === null || o === undefined) {
    return o;
  } else if (typeof o === 'object') {
    const copy = Object.create(
      asType && asType.prototype ? asType.prototype : Object.getPrototypeOf(o)
    );
    // explicit cast
    const record = o as Record<string, any>;
    Object.keys(record).forEach(key => {
      copy[key] = copyDeep(record[key]);
    });
    // convention to have 'init' method if we want to initialize
    if (copy['init'] && typeof copy['init'] === 'function') {
      copy.init();
    }
    return copy;
  } else {
    return o;
  }
};
// either current option or any child matches filter string
export const filterDeep = (o: WOptionOrString, text: string): boolean => {
  const filterString = text.toLowerCase();
  if (typeof o === 'string') {
    return o.toLowerCase().indexOf(filterString) > -1;
  } else {
    // WOption
    const matchLabel = (o.label?.toLowerCase().indexOf(filterString) ?? -1) > -1;
    return matchLabel ||
      (!!o.nestedOptions && o.nestedOptions.some(option => filterDeep(option, filterString)));
  }
};

export const atNextTick = (state: boolean) => {
  const checked = new Promise<boolean>((resolve) => {
    setTimeout(() => {
      resolve(state);
    })
  })
  return checked;
}
export const propChanged = (changes: SimpleChanges, name: string): boolean => {
  const change = changes[name];
  return change && change.currentValue !== change.previousValue;
}
/* breakpoints */
export enum BREAKPOINT {
  md = 768,
  lg = 992,
  xl = 1200,
  larger = -1,
  mdCLF = 800
}
export const getBreakpoint = (width: number): BREAKPOINT => {
  if (width < BREAKPOINT.md) {
    return BREAKPOINT.md;
  } else if (width < BREAKPOINT.lg) {
    return BREAKPOINT.lg;
  } else if (width < BREAKPOINT.xl) {
    return BREAKPOINT.xl;
  } else {
    return BREAKPOINT.larger;
  }
}
export const isMobile = (width: number, cutoff = BREAKPOINT.md): boolean => {
  return getBreakpoint(width) === cutoff;
}
const mobileCutoff = () => BREAKPOINT.md;
export abstract class BehaviorServiceConfig {
  abstract mobile: () => Observable<boolean>;
}
@Injectable()
export class BehaviorServiceConfigDefault extends BehaviorServiceConfig {
  mobile: () => Observable<boolean> =
    () => WUniversalModule.init
      .pipe(
        /** make sure we trigger this once, when status is true */
        filter(status => status),
        distinctUntilChanged(),
        mergeMap(() => isBrowser ?
          fromEvent(window, 'resize')
            .pipe(
              debounceTime(50),
              map(() => {
                return isMobile(window.innerWidth, mobileCutoff());
              }),
              startWith(isMobile(window.innerWidth)),
              distinctUntilChanged()
            ) : of(false)));
}
const stringCache: Map<string, Observable<string>> = new Map();
export const cachedAsObservable = (o: string) => {
  let labelAsync = stringCache.get(o);
  if (!labelAsync) {
    labelAsync = of(o);
    stringCache.set(o, labelAsync);
  }
  return labelAsync;
}
export const observableString = (option: string | Observable<string>): Observable<string> => {
  if (typeof option !== 'string') {
    return option;
  } else {
    return cachedAsObservable(option);
  }
}
/* check if this-or-that elements */
//   return !text || !text.trim();
// };
const hasAttributeLike = (node: HTMLElement, pfx: string): boolean => {
  const p = pfx.toLowerCase();
  return displayed(node) && Array.from(node.attributes).some(a => a.name.toLowerCase().startsWith(p));
};

const hasTagAttrPfx = (e: HTMLElement, pfx: string): boolean => {
  // tag starts with magic string, but not included in container components
  const ok = displayed(e) && ((e.tagName.startsWith(pfx) && CONTAINER.indexOf(e.tagName) === -1) || hasAttributeLike(e, pfx) ||
    Array.from((e as HTMLElement).children).some(c => c.nodeType === Node.ELEMENT_NODE && hasTagAttrPfx(c as HTMLElement, pfx)));
  return ok;
};
const hasTag = (e: HTMLElement, tag: string): boolean => {
  return displayed(e) &&
    (e.tagName === tag ||
      Array.from((e as HTMLElement).children).some(c => c.nodeType === Node.ELEMENT_NODE && hasTag(c as HTMLElement, tag)));
};
// svg and canvas are strictly not empty, but has visual presentation event if not contain text
const NOT_CONTAINER = ['IMG', 'INPUT', 'HR', 'AREA', 'SVG', 'CANVAS', 'BUTTON'];
// w-angular or primeNg container elements (not visible unless have visible content)
const CONTAINER = ['W-SLOT', 'W-STEP', 'W-VIEW-PANEL', 'W-EDIT-PANEL', 'W-SECTION', 'W-VIEW', 'W-ARTICLE', 'W-BOX', 'W-PAY-BOX'];
export const hasEmptyElement = (e: HTMLElement): boolean => {
  return NOT_CONTAINER.some((tag: string) => hasTag(e, tag));
};
export const hasText = (e: HTMLElement, self = false): boolean => {
  if (self || displayed(e)) {
    const hasSelfText = !!e.textContent?.trim();
    const children = Array.from(e.childNodes);
    const t = children.some(n => n.nodeType === Node.TEXT_NODE && n.textContent?.trim());
    return hasSelfText || t || children.some(n => n.nodeType === Node.ELEMENT_NODE && hasText(n as HTMLElement));
  } else return false;
};
export const displayed = (e: Element): boolean => {
  return getComputedStyle(e).display !== 'none';
};

export const maxZIndex = (): number => {
  if (!document) {
    console.warn('%cw-angular', ngstyle, 'maxZIndex: UniversalModule not initialized yet, document is not available');
    return 0;
  }
  return computeMaxZIndex(document.body);
};

const computeMaxZIndex = (parent: HTMLElement, currentMax = 0): number => {
  const computedStyle = window.getComputedStyle(parent);
  const parentZIndex = parseFloat(computedStyle.zIndex);
  let newMax = Math.max(currentMax, isNaN(parentZIndex) ? 0 : parentZIndex);

  if (parent.shadowRoot) {
    const children = Array.from(parent.shadowRoot.children);
    for (const child of children) {
      newMax = computeMaxZIndex(child as HTMLElement, newMax);
    }
  } else {
    const children = Array.from(parent.children);
    for (const child of children) {
      newMax = computeMaxZIndex(child as HTMLElement, newMax);
    }
  }
  return newMax;
};

export function compareSimpleArraysInOrder(
  arr1: (string | number | boolean)[] | undefined,
  arr2: (string | number | boolean)[] | undefined
): boolean {
  // Check if arrays have the same length (and if they are defined at all)
  if (!arr1 || !arr2 || arr1?.length !== arr2?.length) {
    return false;
  }

  // Compare each element at the same index
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  // Arrays are equal
  return true;
}

export function compareObjectArraysInOrder(arr1: any[], arr2: any[]) {
  // Check if arrays have the same length
  if (arr1.length !== arr2.length) {
    return false;
  }

  // Compare each object and its properties at the same index
  for (let i = 0; i < arr1.length; i++) {
    // Get the keys of the current objects
    const keys1 = Object.keys(arr1[i]);
    const keys2 = Object.keys(arr2[i]);

    // Check if objects have the same number of keys
    if (keys1.length !== keys2.length) {
      return false;
    }

    // Check if all keys and values match
    for (let j = 0; j < keys1.length; j++) {
      const key = keys1[j];
      if (arr1[i][key] !== arr2[i][key]) {
        return false;
      }
    }
  }

  // Arrays are equal
  return true;
}
export const getWidestDescendant = (element?: HTMLElement) => {
  if (!element) {
    return 0; // no element, no width
  }
  let maxWidth = 0;
  const descendants = Array.from(element.getElementsByTagName('*'));

  for (let descendant of descendants) {
    const width = descendant.getBoundingClientRect().width;
    if (width > maxWidth) {
      maxWidth = width;
    }
  }

  return maxWidth;
}
export const sigOf = (e: HTMLElement): string => {
  return `${e.tagName}.${Array.from(e.classList)
    .filter(c => !c.startsWith('ng-'))
    .join('.')}${Array.from(e.attributes)
      .filter((a: Attr) => !a.name.startsWith('ng-') && !a.name.startsWith('_ng') &&
        ['class', 'style'].indexOf(a.name) === -1)
      .map((a: Attr) => `[${a.name}]`)}`.trim();
}
/** types */
export type inlinePosition = 'left' | 'right' | 'start' | 'end';